import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { n8nInstance, apiInstance } from "@shared/services";
import { getLocalStorage } from "@shared/constants";
import { PAYFAC_QUERIES } from "./queries";

import { convertObjectToCamelCase, handleApiError } from "@shared/helpers";

import type {
  IRatePlanWithPagination,
  IRatePlanDetails,
  IRatePlanSellerWithPagination,
} from "@payfac/types";

const companyId = getLocalStorage("companyId");
const email = getLocalStorage("userEmail") || "";

const URLS = {
  ratePlans: `/v2/payfac/rate-plans`,
  ratePlanSellers: `e4df297e-a963-4936-811f-330a1fb91bae/prd/v1/companies/${companyId}/sellers`,
};

const n8nApi = n8nInstance({
  enableBearerToken: true,
  enableXTokenApi: true,
});

const api = apiInstance({
  enableBearerToken: true,
  enableXTokenApi: true,
});

export const useGetRatePlansList = ({
  filters,
}: {
  filters?: {
    limit?: number;
    page?: number;
    timestamp?: string;
    webapp?: boolean;
  };
} = {}) => {
  const getRatePlansList = async (): Promise<{
    success: boolean;
    message: string;
    data?: IRatePlanWithPagination;
  }> => {
    try {
      const { data } = await api.get(URLS.ratePlans, {
        params: filters,
      });

      return {
        success: true,
        message: "Registros encontrados com sucesso!",
        data: data as IRatePlanWithPagination,
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao buscar registros!");
    }
  };

  return useQuery({
    queryKey: [PAYFAC_QUERIES.RATE_PLANS, filters],
    queryFn: () => getRatePlansList(),
  });
};

export const useGetRatePlansDetails = ({ planId }: { planId: number }) => {
  const getRatePlansDetails = async (): Promise<{
    success: boolean;
    message: string;
    data?: IRatePlanDetails;
  }> => {
    try {
      const { data } = await api.get(`${URLS.ratePlans}/${planId}`, {
        params: { webapp: true },
      });

      return {
        success: true,
        message: "Detalhes encontrados com sucesso!",
        data: data as IRatePlanDetails,
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao buscar detalhes!");
    }
  };

  return useQuery({
    queryKey: [PAYFAC_QUERIES.RATE_PLANS, planId],
    queryFn: () => getRatePlansDetails(),
    enabled: !!planId,
  });
};

export const useGetRatePlansSellersList = ({
  planId,
  filters,
}: {
  planId: number;
  filters?: {
    limit?: number;
    page?: number;
    timestamp?: string;
  };
}) => {
  const getRatePlansSellersList = async (): Promise<{
    success: boolean;
    message: string;
    data?: IRatePlanSellerWithPagination;
  }> => {
    try {
      const { data } = await n8nApi.get(URLS.ratePlanSellers, {
        params: { ...filters, id_plan_seller: planId, negate: false },
      });

      return {
        success: true,
        message: "Vendedores encontrados com sucesso!",
        data: convertObjectToCamelCase(data) as IRatePlanSellerWithPagination,
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao buscar vendedores!");
    }
  };

  return useQuery({
    queryKey: [PAYFAC_QUERIES.RATE_PLANS_SELLERS, planId, filters],
    queryFn: () => getRatePlansSellersList(),
    enabled: !!planId,
  });
};

export const useUpdateRatePlan = () => {
  const queryClient = useQueryClient();

  const updateRatePlan = async ({
    planId,
    payload,
  }: {
    planId: string;
    payload: IRatePlanDetails;
  }): Promise<{ success: boolean; message: string; data?: any }> => {
    try {
      const { data } = await api.put(`${URLS.ratePlans}/${planId}`, payload, {
        params: { webapp: true, email },
      });

      return {
        success: true,
        message: "Plano atualizado com sucesso!",
        data: convertObjectToCamelCase(data),
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.errors?.[0]?.description ||
          error?.response?.data?.message ||
          error?.response?.message ||
          "Erro ao atualizar o plano!",
        data: error?.response?.data?.errors,
      };
    }
  };

  return useMutation({
    mutationFn: updateRatePlan,
    onSuccess: (response, variables) => {
      if (response.success) {
        queryClient.invalidateQueries({
          queryKey: [PAYFAC_QUERIES.RATE_PLANS, Number(variables.planId)],
        });
        queryClient.invalidateQueries({
          queryKey: [PAYFAC_QUERIES.RATE_PLANS],
        });
      }
    },
  });
};

export const usePartialUpdateRatePlan = () => {
  const queryClient = useQueryClient();

  const partialUpdateRatePlan = async ({
    planId,
    payload,
  }: {
    planId: string;
    payload: Partial<IRatePlanDetails>;
  }): Promise<{ success: boolean; message: string; data?: any }> => {
    try {
      const email = localStorage.getItem("userEmail") || "";

      const { data } = await api.patch(`${URLS.ratePlans}/${planId}`, payload, {
        params: { webapp: true, email },
      });

      return {
        success: true,
        message: "Plano atualizado com sucesso!",
        data: convertObjectToCamelCase(data),
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.errors?.[0]?.description ||
          error?.response?.data?.message ||
          error?.response?.message ||
          "Erro ao atualizar o plano!",
        data: error?.response?.data?.errors,
      };
    }
  };

  return useMutation({
    mutationFn: partialUpdateRatePlan,
    onSuccess: (response, variables) => {
      if (response.success) {
        queryClient.invalidateQueries({
          queryKey: [PAYFAC_QUERIES.RATE_PLANS, Number(variables.planId)],
        });
        queryClient.invalidateQueries({
          queryKey: [PAYFAC_QUERIES.RATE_PLANS],
        });
      }
    },
  });
};

export const useCreateRatePlan = () => {
  const queryClient = useQueryClient();

  const createRatePlan = async ({
    payload,
  }: {
    payload: IRatePlanDetails;
  }): Promise<{ success: boolean; message: string; data?: any }> => {
    try {
      const { data } = await api.post(URLS.ratePlans, payload, {
        params: { webapp: true, email },
      });

      return {
        success: true,
        message: "Plano criado com sucesso!",
        data: convertObjectToCamelCase(data),
      };
    } catch (error: any) {
      return handleApiError(error, "Erro ao criar o plano!");
    }
  };

  return useMutation({
    mutationFn: createRatePlan,
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({
          queryKey: [PAYFAC_QUERIES.RATE_PLANS],
        });
      }
    },
  });
};
